import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getXeroPayLeave, GetXeroPayLeaveOutputType } from 'zite-endpoints-sdk';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, CreditCard, Clock, AlertCircle, ExternalLink, PoundSterling } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

type Data = GetXeroPayLeaveOutputType;

function fmtDate(d?: string) { if (!d) return '—'; try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; } }
function freshness(d?: string) { if (!d) return 'Never'; try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return 'Unknown'; } }

const syncBadge = (status?: string) => {
  if (!status || status === 'Pending') return null;
  const colors: Record<string, string> = { Synced: 'bg-primary/10 text-primary', Syncing: 'bg-accent text-accent-foreground', 'Sync Failed': 'bg-destructive/10 text-destructive' };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[status] || 'bg-muted text-muted-foreground'}`}>{status}</span>;
};

export default function PayLeave() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getXeroPayLeave({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-4">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
      <Skeleton className="h-40" />
    </div>
  );

  if (!data?.connected) return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <PoundSterling className="w-6 h-6 text-primary" />
        <h1 className="text-xl font-bold">Pay & Leave</h1>
      </div>
      <Card><CardContent className="py-12 text-center">
        <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">Xero Payroll is not connected yet.</p>
        <p className="text-sm text-muted-foreground mt-1">Ask your manager or admin to connect Xero in the Admin Panel.</p>
      </CardContent></Card>
    </div>
  );

  if (!data.mapped) return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <PoundSterling className="w-6 h-6 text-primary" />
        <h1 className="text-xl font-bold">Pay & Leave</h1>
      </div>
      <Card><CardContent className="py-12 text-center">
        <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
        <p className="font-medium">Your account isn't linked to Xero yet</p>
        <p className="text-sm text-muted-foreground mt-1">Contact your manager to confirm your Xero employee mapping.</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <PoundSterling className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Pay & Leave</h1>
            <p className="text-sm text-muted-foreground">Your payroll data from Xero · Last refreshed {freshness(data.lastRefreshed)}</p>
          </div>
        </div>
      </div>

      {data.connectionError && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
          <p className="text-sm text-destructive">{data.connectionError} — data may be outdated.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Holiday Balance</p>
            <p className="text-3xl font-bold mt-1">
              {data.holidayBalance !== undefined ? data.holidayBalance : '—'}
              <span className="text-base font-normal text-muted-foreground ml-1">{data.holidayUnit || 'days'}</span>
            </p>
            {data.holidayAllowance !== undefined && <p className="text-xs text-muted-foreground mt-1">of {data.holidayAllowance} {data.holidayUnit || 'days'} allowance</p>}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Latest Payslip</p>
            {data.latestPayslip ? (
              <>
                <p className="text-xl font-bold mt-1">£{data.latestPayslip.netPay.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-muted-foreground mt-1">Paid {fmtDate(data.latestPayslip.paymentDate)} · {data.latestPayslip.periodLabel}</p>
                <Link to="/payslips" className="text-xs text-primary mt-2 inline-flex items-center gap-1">View details <ExternalLink className="w-3 h-3" /></Link>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">No payslips available</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Pending Requests</p>
            <p className="text-3xl font-bold mt-1">{data.pendingLeaveCount}</p>
            {data.recentLeave.filter(l => l.status === 'Submitted').length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">{data.recentLeave.find(l => l.status === 'Submitted')?.type} · {fmtDate(data.recentLeave.find(l => l.status === 'Submitted')?.startDate)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">Recent Leave</h2>
          <Link to="/leave"><Button size="sm">Request Leave</Button></Link>
        </div>
        {data.recentLeave.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No leave requests yet</CardContent></Card>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {data.recentLeave.map(l => (
              <div key={l.id} className="flex items-center justify-between p-3">
                <div>
                  <p className="text-sm font-medium">{l.type}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(l.startDate)} — {fmtDate(l.endDate)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {syncBadge(l.xeroSyncStatus)}
                  <Badge variant={l.status === 'Approved' ? 'default' : l.status === 'Rejected' ? 'destructive' : 'secondary'}>{l.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
